import { logger } from "@/utils/logger";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface LocationCoordinates {
	latitude: number;
	longitude: number;
	accuracy?: number;
	altitude?: number | null;
	altitudeAccuracy?: number | null;
	heading?: number | null;
	speed?: number | null;
}

export interface LocationPermissionState {
	// Permission state
	permissionStatus: PermissionState | null;
	isPermissionSupported: boolean;

	// Location data
	currentLocation: LocationCoordinates | null;
	lastLocationUpdate: number | null;

	// Loading states
	isRequestingPermission: boolean;
	isGettingLocation: boolean;
	isGPSOff: boolean;

	// Error handling
	lastError: GeolocationPositionError | null;
	errorMessage: string | null;

	// Settings
	watchId: number | null;
	isWatching: boolean;

	// Actions
	checkPermissionStatus: () => Promise<PermissionState>;
	requestLocationPermission: () => Promise<{
		isPermissionSupported: boolean;
		currentLocation: LocationCoordinates | null;
		isWatching: boolean;
		isGPSOff: boolean;
	}>;
	getCurrentLocation: (options?: PositionOptions) => Promise<GeolocationPosition>;
	watchLocation: (options?: PositionOptions) => Promise<number>;
	stopWatching: () => void;
	clearLocation: () => void;
	clearError: () => void;

	// Utility methods
	isLocationAvailable: () => boolean;
	isLocationStale: (maxAgeMs?: number) => boolean;
	getLocationAge: () => number | null;
	hasPermission: () => boolean;
	isPermissionDenied: () => boolean;
}

const DEFAULT_POSITION_OPTIONS: PositionOptions = {
	enableHighAccuracy: true,
	timeout: 10000,
	maximumAge: 300000, // 5 minutes
};

export const useLocationPermissionStore = create(
	devtools(
		immer(
			(set, get): LocationPermissionState => ({
				// Initial state
				permissionStatus: null,
				isPermissionSupported: typeof navigator !== "undefined" && "permissions" in navigator,
				currentLocation: null,
				lastLocationUpdate: null,
				isRequestingPermission: false,
				isGettingLocation: false,
				lastError: null,
				errorMessage: null,
				watchId: null,
				isWatching: false,
				isGPSOff: false, // GPS status unknown initially

				// Check current permission status
				checkPermissionStatus: async () => {
					if (typeof navigator === "undefined" || !("permissions" in navigator)) {
						logger.info("Permission check: Geolocation API not supported");
						return "prompt";
					}

					try {
						const result = await navigator.permissions.query({ name: "geolocation" });
						set(state => {
							state.permissionStatus = result.state;
						});

						logger.info("Permission check result:", {
							permissionStatus: result.state,
							timestamp: Date.now(),
						});

						// Listen for permission changes
						result.addEventListener("change", () => {
							set(state => {
								state.permissionStatus = result.state;

								// Clear location if permission is denied
								if (result.state === "denied") {
									state.currentLocation = null;
									state.lastLocationUpdate = null;
								}
							});

							logger.info("Permission status changed:", {
								newStatus: result.state,
								timestamp: Date.now(),
							});
						});

						return result.state;
					} catch (error) {
						logger.warn("Permission check failed:", error);
						return "prompt";
					}
				},

				// Request location permission
				requestLocationPermission: async () => {
					if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
						set(state => {
							state.errorMessage = "Not supported";
							state.isGPSOff = false;
						});
						const { isPermissionSupported, currentLocation, isWatching, isGPSOff } = get();
						logger.info("Location permission request: Geolocation not supported");
						return { isPermissionSupported, currentLocation, isWatching, isGPSOff };
					}

					set(state => {
						state.isRequestingPermission = true;
						state.isGPSOff = false;
						state.errorMessage = null;
						state.lastError = null;
					});

					logger.info("Location permission request started");

					try {
						// Step 1: Trigger browser permission with low accuracy
						await new Promise((resolve, reject) => {
							navigator.geolocation.getCurrentPosition(resolve, reject, {
								enableHighAccuracy: true,
								timeout: 30000,
							});
						});

						logger.info("Browser permission granted, testing GPS");

						// Step 2: Test GPS with high accuracy
						try {
							await new Promise((resolve, reject) => {
								navigator.geolocation.getCurrentPosition(resolve, reject, {
									enableHighAccuracy: false,
									timeout: 30000,
								});
							});

							// Step 3: Both browser permission and GPS work
							set(state => {
								state.isRequestingPermission = false;
								state.permissionStatus = "granted";
								state.isGPSOff = false;
							});

							logger.info("Location permission request successful", {
								outcome: "success",
								permissionStatus: "granted",
								isGPSOff: false,
							});
						} catch (gpsError) {
							// Step 4: Browser permission works but GPS/high-accuracy fails
							// Use duck typing instead of instanceof since GeolocationPositionError instanceof checks can be unreliable
							const isGeolocationError =
								gpsError && typeof gpsError === "object" && "code" in gpsError && "message" in gpsError;

							if (isGeolocationError) {
								const geoError = gpsError as GeolocationPositionError;

								if (geoError.code === 2 || geoError.code === 3) {
									// POSITION_UNAVAILABLE (2) or TIMEOUT (3) - likely GPS-related issues
									// TIMEOUT often occurs when GPS is enabled mid-request
									set(state => {
										state.isRequestingPermission = false;
										state.permissionStatus = "granted"; // Browser permission granted
										state.isGPSOff = true; // Flag indicates GPS issue detected
										state.errorMessage = "Please turn on your device's Location/GPS switch.";
										state.lastError = geoError;
									});

									logger.warn("Location permission request: GPS detected as off", {
										outcome: "gps_off",
										permissionStatus: "granted",
										isGPSOff: true,
										errorCode: geoError.code,
										errorMessage: geoError.message,
									});
								} else {
									// Other GPS errors (PERMISSION_DENIED = 1, or unknown codes)
									set(state => {
										state.isRequestingPermission = false;
										state.permissionStatus = "granted"; // Browser permission still granted
										state.isGPSOff = false; // GPS not confirmed off
										state.errorMessage = "GPS location request failed.";
										state.lastError = geoError;
									});

									logger.warn("Location permission request: GPS test failed", {
										outcome: "gps_test_failed",
										permissionStatus: "granted",
										isGPSOff: false,
										errorCode: geoError.code,
										errorMessage: geoError.message,
									});
								}
							} else {
								// Non-GeolocationPositionError - handle gracefully
								set(state => {
									state.isRequestingPermission = false;
									state.permissionStatus = "granted";
									state.isGPSOff = false;
									state.errorMessage = "GPS test encountered an unexpected error.";
									state.lastError = gpsError as GeolocationPositionError;
								});

								logger.debug("Location permission request: GPS test error (non-geolocation)", {
									outcome: "gps_test_error",
									permissionStatus: "granted",
									isGPSOff: false,
									error: gpsError,
								});
							}
						}
					} catch (permissionError) {
						// Step 5: Browser permission failed
						set(state => {
							state.isRequestingPermission = false;
							// Use duck typing instead of instanceof for reliable error detection
							const isGeolocationError =
								permissionError &&
								typeof permissionError === "object" &&
								"code" in permissionError &&
								"message" in permissionError;

							if (isGeolocationError) {
								const geoError = permissionError as GeolocationPositionError;
								state.lastError = geoError;
								if (geoError.code === 1) {
									// PERMISSION_DENIED
									state.permissionStatus = "denied";
									state.errorMessage = "Browser permission denied.";
								} else if (geoError.code === 3) {
									// TIMEOUT (user likely didn't respond to prompt)
									state.permissionStatus = "prompt";
									state.errorMessage =
										"Location request timed out. Please respond to the permission prompt and try again.";
								} else {
									state.errorMessage = "Unable to access location services.";
								}
							} else {
								// Non-GeolocationPositionError
								state.errorMessage = "Permission request encountered an unexpected error.";
								state.lastError = permissionError as GeolocationPositionError;
							}
							state.isGPSOff = false;
						});

						logger.warn("Location permission request: Browser permission failed", {
							outcome: "permission_failed",
							permissionStatus: get().permissionStatus,
							isGPSOff: false,
							error: permissionError,
						});
					}

					const { isPermissionSupported, currentLocation, isWatching, isGPSOff } = get();
					return { isPermissionSupported, currentLocation, isWatching, isGPSOff };
				},

				// Get current location
				getCurrentLocation: (options = DEFAULT_POSITION_OPTIONS) => {
					return new Promise<GeolocationPosition>((resolve, reject) => {
						if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
							const error = new Error("Geolocation not supported");
							set(state => {
								state.errorMessage = error.message;
							});
							reject(error);
							return;
						}

						set(state => {
							state.isGettingLocation = true;
							state.errorMessage = null;
							state.lastError = null;
						});

						navigator.geolocation.getCurrentPosition(
							position => {
								set(state => {
									state.isGettingLocation = false;
									state.currentLocation = {
										latitude: position.coords.latitude,
										longitude: position.coords.longitude,
										accuracy: position.coords.accuracy,
										altitude: position.coords.altitude,
										altitudeAccuracy: position.coords.altitudeAccuracy,
										heading: position.coords.heading,
										speed: position.coords.speed,
									};
									state.lastLocationUpdate = Date.now();
									state.permissionStatus = "granted";
								});
								resolve(position);
							},
							error => {
								set(state => {
									state.isGettingLocation = false;
									state.lastError = error;

									// Use duck typing for consistent error handling
									const isGeolocationError =
										error && typeof error === "object" && "code" in error && "message" in error;

									if (isGeolocationError) {
										const geoError = error as GeolocationPositionError;
										switch (geoError.code) {
											case 1: // PERMISSION_DENIED
												state.permissionStatus = "denied";
												state.isGPSOff = false;
												state.errorMessage = "Location permission denied";
												break;
											case 2: // POSITION_UNAVAILABLE
												state.isGPSOff = true;
												state.errorMessage = "Location information unavailable";
												break;
											case 3: // TIMEOUT
												// Timeout is frequently GPS/availability-related on mobile
												state.isGPSOff = true;
												state.errorMessage = "Location request timed out";
												break;
											default:
												state.isGPSOff = false;
												state.errorMessage = "An unknown error occurred";
										}
									} else {
										state.isGPSOff = false;
										state.errorMessage = "Location request encountered an unexpected error";
									}
								});
								reject(error);
							},
							options,
						);
					});
				},

				// Watch location changes
				watchLocation: async (options = DEFAULT_POSITION_OPTIONS) => {
					return new Promise<number>((resolve, reject) => {
						if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
							const error = new Error("Geolocation not supported");
							set(state => {
								state.errorMessage = error.message;
							});
							reject(error);
							return;
						}

						// Stop existing watch if any
						const currentWatchId = get().watchId;
						if (currentWatchId !== null) {
							navigator.geolocation.clearWatch(currentWatchId);
						}

						const watchId = navigator.geolocation.watchPosition(
							position => {
								set(state => {
									state.currentLocation = {
										latitude: position.coords.latitude,
										longitude: position.coords.longitude,
										accuracy: position.coords.accuracy,
										altitude: position.coords.altitude,
										altitudeAccuracy: position.coords.altitudeAccuracy,
										heading: position.coords.heading,
										speed: position.coords.speed,
									};
									state.lastLocationUpdate = Date.now();
									state.permissionStatus = "granted";
									state.isWatching = true;
									state.errorMessage = null;
									state.lastError = null;
								});
							},
							error => {
								set(state => {
									state.lastError = error;
									state.isWatching = false;

									// Use duck typing for consistent error handling
									const isGeolocationError =
										error && typeof error === "object" && "code" in error && "message" in error;

									if (isGeolocationError) {
										const geoError = error as GeolocationPositionError;
										switch (geoError.code) {
											case 1: // PERMISSION_DENIED
												state.permissionStatus = "denied";
												state.errorMessage = "Location permission denied";
												break;
											case 2: // POSITION_UNAVAILABLE
												state.errorMessage = "Location information unavailable";
												break;
											case 3: // TIMEOUT
												state.errorMessage = "Location request timed out";
												break;
											default:
												state.errorMessage = "An unknown error occurred";
										}
									} else {
										state.errorMessage = "Location watch encountered an unexpected error";
									}
								});
								reject(error);
							},
							options,
						);

						set(state => {
							state.watchId = watchId;
							state.isWatching = true;
						});

						resolve(watchId);
					});
				},

				// Stop watching location
				stopWatching: () => {
					const watchId = get().watchId;
					if (watchId !== null && typeof navigator !== "undefined" && "geolocation" in navigator) {
						navigator.geolocation.clearWatch(watchId);
					}

					set(state => {
						state.watchId = null;
						state.isWatching = false;
					});
				},

				// Clear location data
				clearLocation: () => {
					set(state => {
						state.currentLocation = null;
						state.lastLocationUpdate = null;
						state.errorMessage = null;
						state.lastError = null;
					});
				},

				// Clear error
				clearError: () => {
					set(state => {
						state.errorMessage = null;
						state.lastError = null;
					});
				},

				// Utility methods
				isLocationAvailable: () => {
					const state = get();
					return state.currentLocation !== null;
				},

				isLocationStale: (maxAgeMs = 300000) => {
					// Default 5 minutes
					const state = get();
					if (!state.lastLocationUpdate) return true;
					return Date.now() - state.lastLocationUpdate > maxAgeMs;
				},

				getLocationAge: () => {
					const state = get();
					if (!state.lastLocationUpdate) return null;
					return Date.now() - state.lastLocationUpdate;
				},

				hasPermission: () => {
					const state = get();
					return state.permissionStatus === "granted";
				},

				isPermissionDenied: () => {
					const state = get();
					return state.permissionStatus === "denied";
				},
			}),
		),
		{ name: "location-permission-store" },
	),
);
